import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Image,
  Linking,
  type LayoutChangeEvent,
  type LayoutRectangle,
  Platform,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { BlurView } from "expo-blur";
import { GlassView, isGlassEffectAPIAvailable } from "expo-glass-effect";
import { LinearGradient } from "expo-linear-gradient";
import { StatusBar } from "expo-status-bar";
import { KeyRound, MessageCircle } from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../App";
import type { OnboardingStackParamList } from "../types";
import { useStytchUser } from "../../utils/useStytch";
import { useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { getOnboardingViewport } from "../onboardingViewport";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import LivingCellularBackground from "../components/LivingCellularBackground";
import GlassChoiceButton from "../components/GlassChoiceButton";
import {
  MovingGradientTitle,
  OnboardingPrimaryButton,
} from "../components/OnboardingLayout";
import { semanticColors } from "@louise/theme/tokens";

type Props = NativeStackScreenProps<OnboardingStackParamList, "IntroSplash">;
type RippleOrigin = { x: number; y: number };

const LOUISE_MARK = require("../../assets/images/louise-mark-white.png");
const LOUISE_TEXT_LINK = "sms:+13106840840";

function fixtureDisablesMotion() {
  if (Platform.OS !== "web" || typeof window === "undefined") return false;
  return (
    new URLSearchParams(window.location.search).get("dev_splash_motion") ===
    "off"
  );
}

export default function IntroSplashScreen({ navigation }: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("IntroSplashScreen");
  const { user } = useStytchUser();
  const { width: viewportWidth, height: viewportHeight } =
    useWindowDimensions();
  const { desktop: desktopLayout, condensed: condensedLayout } =
    getOnboardingViewport(viewportWidth, viewportHeight);
  const motionEnabled = !useReducedMotion() && !fixtureDisablesMotion();
  const [rippleOrigin, setRippleOrigin] = useState<RippleOrigin | null>(null);
  const [backgroundReady, setBackgroundReady] = useState(Platform.OS !== "web");
  const hasIOSGlassEffect =
    Platform.OS === "ios" && isGlassEffectAPIAvailable();
  const entrance = useRef(new Animated.Value(0)).current;
  const stackLayout = useRef<LayoutRectangle | null>(null);
  const markLayout = useRef<LayoutRectangle | null>(null);

  const updateRippleOrigin = (
    stack: LayoutRectangle | null,
    mark: LayoutRectangle | null,
  ) => {
    if (!stack || !mark || viewportWidth <= 0 || viewportHeight <= 0) return;

    const nextOrigin = {
      x: Math.min(1, Math.max(0, (stack.x + stack.width / 2) / viewportWidth)),
      y: Math.min(
        1,
        Math.max(0, (stack.y + mark.y + mark.height / 2) / viewportHeight),
      ),
    };

    setRippleOrigin((current) =>
      current &&
      Math.abs(current.x - nextOrigin.x) < 0.0005 &&
      Math.abs(current.y - nextOrigin.y) < 0.0005
        ? current
        : nextOrigin,
    );
  };

  const handleStackLayout = (event: LayoutChangeEvent) => {
    stackLayout.current = event.nativeEvent.layout;
    updateRippleOrigin(stackLayout.current, markLayout.current);
  };

  const handleMarkLayout = (event: LayoutChangeEvent) => {
    markLayout.current = event.nativeEvent.layout;
    updateRippleOrigin(stackLayout.current, markLayout.current);
  };

  const handleBackgroundFirstFrame = useCallback(() => {
    setBackgroundReady(true);
  }, []);

  useLayoutEffect(() => {
    if (!user) return;
    navigation
      .getParent<NativeStackScreenProps<RootStackParamList>["navigation"]>()
      ?.reset({ index: 0, routes: [{ name: "Home" }] });
  }, [navigation, user]);

  useEffect(() => {
    Animated.timing(entrance, {
      toValue: 1,
      duration: motionEnabled ? 700 : 0,
      useNativeDriver: true,
    }).start();
  }, [entrance, motionEnabled]);

  if (user) return null;

  const openLogin = () => navigation.navigate("Login");

  const enterOnboarding = () => navigation.navigate("WelcomeGoals");

  const textLouise = () => {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.open(LOUISE_TEXT_LINK, "_self");
      return;
    }

    void Linking.openURL(LOUISE_TEXT_LINK);
  };

  return (
    <View style={styles.root}>
      <StatusBar style="light" />
      <LivingCellularBackground
        motionEnabled={motionEnabled}
        onFirstFrame={handleBackgroundFirstFrame}
        origin={rippleOrigin}
      />
      <LinearGradient
        pointerEvents="none"
        colors={[
          "rgba(21, 8, 2, 0.12)",
          "rgba(21, 8, 2, 0.00)",
          "rgba(14, 6, 2, 0.44)",
        ]}
        locations={[0, 0.5, 1]}
        style={StyleSheet.absoluteFill}
      />

      <SafeAreaView
        style={[styles.safeArea, desktopLayout && styles.safeAreaDesktop]}
      >
        <Animated.View
          onLayout={handleStackLayout}
          style={[
            styles.bottomStack,
            !hasIOSGlassEffect && {
              opacity: entrance,
            },
            Platform.OS !== "web" && {
              transform: [
                {
                  translateY: entrance.interpolate({
                    inputRange: [0, 1],
                    outputRange: [24, 0],
                  }),
                },
              ],
            },
          ]}
        >
          <View
            style={[
              styles.glassCard,
              Platform.OS === "android" && styles.glassCardAndroid,
              hasIOSGlassEffect && styles.glassCardIOS,
              desktopLayout && styles.glassCardDesktop,
            ]}
          >
            {hasIOSGlassEffect ? (
              <GlassView
                pointerEvents="none"
                glassEffectStyle="clear"
                tintColor="rgba(71, 28, 10, 0.10)"
                colorScheme="dark"
                style={[
                  StyleSheet.absoluteFill,
                  styles.glassEffect,
                  desktopLayout && styles.glassEffectDesktop,
                ]}
              />
            ) : Platform.OS !== "android" ? (
              <BlurView
                key={
                  backgroundReady ? "background-ready" : "background-loading"
                }
                pointerEvents="none"
                intensity={34}
                tint="dark"
                experimentalBlurMethod="dimezisBlurView"
                style={StyleSheet.absoluteFill}
              />
            ) : null}
            {!hasIOSGlassEffect && (
              <View pointerEvents="none" style={styles.glassWash} />
            )}
            <Animated.View
              style={[
                styles.cardContent,
                hasIOSGlassEffect && { opacity: entrance },
                Platform.OS === "web" && {
                  transform: [
                    {
                      translateY: entrance.interpolate({
                        inputRange: [0, 1],
                        outputRange: [24, 0],
                      }),
                    },
                  ],
                },
              ]}
            >
              <Image
                accessibilityLabel="Louise"
                onLayout={handleMarkLayout}
                source={LOUISE_MARK}
                resizeMode="contain"
                style={styles.mark}
              />

              <MovingGradientTitle style={styles.title}>
                {t("onboarding.splash.title")}
              </MovingGradientTitle>
              <Text style={styles.body}>{t("onboarding.splash.body")}</Text>

              <OnboardingPrimaryButton
                title={t("onboarding.splash.get_started")}
                onPress={enterOnboarding}
                style={styles.primaryButton}
              />

              <View style={styles.secondaryButton}>
                <GlassChoiceButton
                  Icon={MessageCircle}
                  label={t("onboarding.splash.text_louise")}
                  onPress={textLouise}
                  selected={false}
                  compact={condensedLayout}
                  borderRadius={29}
                  centered
                  showChevron={false}
                  showBackdrop={false}
                  accessibilityLabel={t("onboarding.splash.text_louise")}
                />
              </View>

              <View style={styles.loginPrompt}>
                <Text style={styles.loginPromptText}>
                  {t("onboarding.splash.already_have_account")}
                </Text>
                <KeyRound
                  size={13}
                  color={semanticColors.onDark.text.secondary}
                  strokeWidth={1.7}
                />
                <Text
                  accessibilityRole="link"
                  onPress={() => {
                    capturePress(
                      pressLabel(t("onboarding.splash.log_in")),
                      "onboarding.splash.log_in",
                    );
                    openLogin();
                  }}
                  style={styles.loginLink}
                >
                  {t("onboarding.splash.log_in")}
                </Text>
              </View>
            </Animated.View>
          </View>
        </Animated.View>
      </SafeAreaView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    minHeight: 520,
    overflow: "hidden",
    backgroundColor: "#1A0A02",
  },
  safeArea: {
    flex: 1,
    width: "100%",
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 12,
    justifyContent: "flex-end",
    alignItems: "center",
  },
  safeAreaDesktop: {
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 32,
  },
  bottomStack: {
    width: "100%",
    maxWidth: 520,
  },
  mark: {
    width: 54,
    height: 54,
    marginBottom: 30,
  },
  glassCard: {
    overflow: "hidden",
    borderTopLeftRadius: 260,
    borderTopRightRadius: 260,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
    borderWidth: 1,
    borderColor: "rgba(255, 255, 255, 0.14)",
    backgroundColor: "rgba(20, 13, 9, 0.32)",
    shadowColor: "#130702",
    shadowOffset: { width: 0, height: 14 },
    shadowOpacity: 0.26,
    shadowRadius: 34,
  },
  glassCardDesktop: {
    borderRadius: 28,
  },
  glassCardAndroid: {
    backgroundColor: "rgba(71, 28, 10, 0.62)",
  },
  glassCardIOS: {
    backgroundColor: "transparent",
  },
  glassEffect: {
    borderTopLeftRadius: 260,
    borderTopRightRadius: 260,
    borderBottomLeftRadius: 28,
    borderBottomRightRadius: 28,
  },
  glassEffectDesktop: {
    borderRadius: 28,
  },
  glassWash: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(55, 42, 34, 0.13)",
  },
  cardContent: {
    alignItems: "center",
    paddingHorizontal: 28,
    paddingTop: 32,
    paddingBottom: 24,
  },
  title: {
    maxWidth: 400,
    fontFamily: "Tobias_300Light",
    color: semanticColors.onDark.text.primary,
    fontSize: 37,
    lineHeight: 40,
    letterSpacing: -1,
    textAlign: "center",
  },
  body: {
    maxWidth: 380,
    marginTop: 18,
    fontFamily: "DMSans_400Regular",
    color: semanticColors.onDark.text.secondary,
    fontSize: 14,
    lineHeight: 20,
    textAlign: "center",
  },
  primaryButton: {
    marginTop: 28,
  },
  secondaryButton: {
    width: "100%",
    marginTop: 12,
  },
  loginPrompt: {
    marginTop: 18,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
  },
  loginPromptText: {
    fontFamily: "DMSans_400Regular",
    color: semanticColors.onDark.text.tertiary,
    fontSize: 13,
    lineHeight: 18,
  },
  loginLink: {
    color: semanticColors.onDark.text.primary,
    fontFamily: "DMSans_500Medium",
    textDecorationLine: "underline",
  },
});
